import { useEffect, useState } from 'react';

interface CountUpOptions {
  /** When false (e.g. prefers-reduced-motion), the hook returns `target` immediately. */
  enabled?: boolean;
  /** Animation duration in ms. */
  duration?: number;
  /** Delay before the count starts, in ms. */
  delay?: number;
}

/**
 * Animates a number from 0 to `target` with an ease-out-quart curve,
 * driven by requestAnimationFrame. Self-cancels on unmount.
 */
export function useCountUp(
  target: number,
  { enabled = true, duration = 900, delay = 0 }: CountUpOptions = {},
): number {
  const [value, setValue] = useState(enabled ? 0 : target);

  useEffect(() => {
    if (!enabled) {
      setValue(target);
      return;
    }
    let raf = 0;
    let start: number | null = null;
    const tick = (now: number) => {
      if (start === null) start = now + delay;
      const t = Math.min(1, Math.max(0, (now - start) / duration));
      setValue(target * (1 - Math.pow(1 - t, 4)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, enabled, duration, delay]);

  return value;
}
